import { Services } from "@ca.vmedia.tv/services";
import { VodContentType, OnDemandData, type OnDemandItem } from "@ca.vmedia.tv/domains";
import { AppConstants } from "@/common";
import { OnDemandCardData} from "@/components/Cards/types";
import { Logger } from '@/utils';
import { useVodPageStore } from "@/stores/VodPage";
import { useEmptyLine } from "@/composables/useEmptyLine";
import type { ExposeInterface } from './types';
import type { AxiosError } from "axios";

export const useMultiLinesVodGenresStore = defineStore('MultiLinesVodGenres', (): ExposeInterface => {
    const {
        emptyOnDemandData
    } = useEmptyLine();

    const VodPageStore = useVodPageStore();
    const {
        getCurrentGenre,
    } = storeToRefs(VodPageStore);


    const _showsGenres: Ref<OnDemandData | null> = ref(null);
    const fetchShowsGenres = async (): Promise<void> => {
        try {
            if (!getCurrentGenre.value) {
                throw new Error("Genre is null in fetchShowsGenres");
            }
            if (_showsGenres.value !== null) {
                return;
            }

            _showsGenres.value = await Services.Instance.vod.getVodContent(
                VodContentType.AllShows,
                AppConstants.ItemsInLine,
                1,
                getCurrentGenre.value,
            );
        } catch (error) {
            _showsGenres.value = emptyOnDemandData();
            Logger.captureError(error as AxiosError | Error);
        }
    };
    const getCardsShowsGenres = computed((): (OnDemandCardData)[] | null => {
        const data = _showsGenres.value;

        if (!data?.items) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        return data.items.map((item) => new OnDemandCardData(item));
        
    });
    const _isLoadingShows: Ref<boolean> = ref(false);
    const getIsLoadingShows: ComputedRef<boolean> = computed(() => _isLoadingShows.value);
    const _setIsLoadingShows = (isLoading: boolean) => {
        _isLoadingShows.value = isLoading;
    };
    const loadShowsGenresNextPage = async () => {
        if( _isLoadingShows.value === true){
            return;
        }
        const pageNumber = !_showsGenres.value?.items || !_showsGenres.value?.paging?.itemsOnPage
        ? -1
        : Math.ceil(_showsGenres.value.items.length / _showsGenres.value.paging.itemsOnPage) + 1;
        if(pageNumber < 0 || !_showsGenres.value?.paging?.totalPages || pageNumber > _showsGenres.value.paging?.totalPages){
            return;
        }

        try {
            if (!getCurrentGenre.value) {
                throw new Error("Genre is null in loadShowsGenresNextPage");
            }
            _setIsLoadingShows(true);

            const data = await Services.Instance.vod.getVodContent(
                VodContentType.AllShows,
                AppConstants.ItemsInLine,
                pageNumber,
                getCurrentGenre.value,
            );
    
            if (data?.items?.length) {
                const items = (_showsGenres.value.items as OnDemandItem[]).concat(data.items);
                _showsGenres.value = new OnDemandData(items, null, _showsGenres.value.paging);
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        } finally {
            _setIsLoadingShows(false);
        }
    };

    const clearMultiLinesVodGenres =  (): void => {
        _showsGenres.value = null;
    };


    return {
        clearMultiLinesVodGenres,
        getCardsShowsGenres,
        fetchShowsGenres,
        getIsLoadingShows,
        loadShowsGenresNextPage,
    };
});